import { type QueryClient, useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'
import type { AttestationConsent } from './attestation'
import { FillApi, fillApplicationUrl, type FillCheck, type FillOpeningState } from './fillApplication'
import type { ContractorAnswer } from './insuranceApplication'
import type { EnabledCoverage } from './insuranceCoverage'
import type { OrgStatus } from './user'

/** Where the customer's review page lives; /application hands over to it at the chat's review. */
export const CONFIRM_INFO_PATH = '/insurance/confirm-info'

/** The chat's steps from its review until carriers are asked, which the page runs. */
export const CONFIRM_INFO_SERVICES = ['CONFIRM_INFORMATION', 'CONFIRM_INFORMATION_VERIFY_EMAIL']

/** Where the Confirmation tab stands for the person signed in. */
export enum ConfirmInfoStage {
  REVIEW = 'REVIEW',
  VERIFY_EMAIL = 'VERIFY_EMAIL',
  /** Carriers were asked: the quotes page takes over. */
  SENT = 'SENT',
}

/** A refusal the page answers by going somewhere else. */
export enum ConfirmInfoRefusal {
  /** The chat left its prior-claims question to a file that was still being read: it asks it now. */
  PRIOR_CLAIMS = 'PRIOR_CLAIMS',
  /** A coverage added on the review brought questions the chat hasn't asked: it asks them now. */
  QUESTIONS_IN_CHAT = 'QUESTIONS_IN_CHAT',
}

export enum ContractorQuestionKind {
  TEXT = 'TEXT',
  LONG_TEXT = 'LONG_TEXT',
  NUMBER = 'NUMBER',
  CHOICE = 'CHOICE',
  MULTI = 'MULTI',
}

/** The answer a contractor may skip, as the chat's "Skip for now" sends it. */
export const SKIPPED_ANSWER = 'SKIP'

/** How the review edits one answer, holding the answer as stored. */
export interface ContractorAnswerField {
  kind: ContractorQuestionKind
  options: { value: string; label: string }[]
  placeholder: string
  optional: boolean
  /** A NUMBER in dollars. */
  money: boolean
  maxLength: number
  value: string | number | string[]
}

export interface ContractorAnswerRow extends ContractorAnswer {
  /** Null for an answer the review shows but doesn't edit. */
  field: ContractorAnswerField | null
}

/** One part of a contractor's answers, as the review shows it. */
export interface ContractorAnswerSection {
  title: string
  rows: ContractorAnswerRow[]
}

/** What a contractor's review shows instead of trucking's parts. */
export interface ContractorReview {
  sections: ContractorAnswerSection[]
  coverageMenu: EnabledCoverage[]
  /** Auto is among the coverages, so the review shows the vehicles and drivers. */
  vehicles: boolean
}

export enum EmailCodeAction {
  RESEND = 'RESEND',
  CHANGE_EMAIL = 'CHANGE_EMAIL',
}

export interface ConfirmInfoState {
  application: { id: string; created: string } | null
  orgStatus: OrgStatus
  /** READ_ONLY, FMCSA or FORM: there is no documents screen here. */
  openingState: FillOpeningState
  stage: ConfirmInfoStage
  checks: FillCheck[]
  /** The signed-in person's address, which the email code goes to. */
  email: string
  confirmedAt: string | null
  eld: { connected: boolean; providerName: string }
  /** Set for a contractor's application, which the page reviews in its own parts. */
  contractor: ContractorReview | null
}

export interface NewTeammate {
  firstName: string
  lastName: string
  email: string
}

function stateKey(orgId: string | undefined) {
  // Under Intake's key, so the card saves that refresh its state refresh this one too.
  return [CacheKeys.FILL_APPLICATION_STATE, orgId, FillApi.CUSTOMER]
}

function asState(response: { data: unknown }): ConfirmInfoState {
  return toCamelCase(response.data) as ConfirmInfoState
}

export function useConfirmInfoState(orgId: string | undefined) {
  const { data, isLoading, isError } = useQuery({
    queryKey: stateKey(orgId),
    queryFn: () => axios.get(fillApplicationUrl(orgId!, 'state/', FillApi.CUSTOMER)).then(asState),
    enabled: !!orgId,
    // A new object per fetch, unchanged or not: the page reads a fresh `checks` as the server having looked again.
    structuralSharing: false,
  })
  return { state: data, isLoading, isError }
}

export function setConfirmInfoState(queryClient: QueryClient, orgId: string, state: ConfirmInfoState) {
  queryClient.setQueryData(stateKey(orgId), state)
}

/** Records Cai's review certification; the state comes back on the step after it. */
export function confirmApplication(
  orgId: string,
  attestations: AttestationConsent[]
): Promise<ConfirmInfoState> {
  return axios.post(fillApplicationUrl(orgId, 'confirm/', FillApi.CUSTOMER), { attestations }).then(asState)
}

/** Replaces answers a contractor gave in the chat; refused per question as `{key: message}`. */
export function saveContractorAnswers(
  orgId: string,
  answers: Record<string, ContractorAnswerField['value']>
): Promise<ConfirmInfoState> {
  return axios
    .post(fillApplicationUrl(orgId, 'contractor-answers/', FillApi.CUSTOMER), { answers })
    .then(asState)
}

export function verifyConfirmEmail(
  orgId: string,
  body:
    | { code: string }
    | { action: EmailCodeAction.RESEND }
    | { action: EmailCodeAction.CHANGE_EMAIL; email: string }
): Promise<ConfirmInfoState> {
  return axios.post(fillApplicationUrl(orgId, 'verify-email/', FillApi.CUSTOMER), body).then(asState)
}

/** Adds the teammate to the customer and emails them a link that signs them in here. */
export function shareApplication(
  orgId: string,
  teammate: NewTeammate
): Promise<{ name: string; email: string }> {
  return axios
    .post(fillApplicationUrl(orgId, 'share/', FillApi.CUSTOMER), toSnakeCase(teammate))
    .then((response) => response.data as { name: string; email: string })
}
